import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { COUNTRIES_WITH_STATES } from 'addresses/constants';
import { addressService } from 'addresses/services';
import { ICountryAutocomplete, IStateAutocomplete } from 'addresses/types';
import { IAuthUser } from 'auth/types';
import { Form, FormField } from 'core/components';
import { FormGroupWrapper } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { getDateOptions } from 'core/utils';
import { corporateService } from 'corporate/services';
import { IVerticalMarket } from 'corporate/types';
import { IAddProjectForm } from 'projects/types';

interface IProps {
  isOpen: boolean;
  onClose: (reset: UseFormReset<IAddProjectForm>) => void;
  onSubmit: (reset?: UseFormReset<IAddProjectForm>) => (data: IAddProjectForm) => void;
  user: IAuthUser;
  level?: number;
  isEdit?: boolean;
}

export const UpsertProjectFormFields = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  level = 0,
  isEdit = false,
}: IProps) => {
  const { t } = useTranslation();

  const { watch } = useFormContext();

  const countryValue = watch('address[country]');

  const isStateDropdown = COUNTRIES_WITH_STATES.includes(countryValue?.abbreviation);

  const StateField = isStateDropdown ? (
    <FormField
      key={watch('address[country]')?.abbreviation}
      name="address[state]"
      type={InputType.AUTOCOMPLETE}
      inputProps={{
        placeholder: t('labelState'),
        getOptions: addressService.getStatesAutocomplete,
        requestPayload: {
          filters: {
            countryAbbreviation: countryValue?.abbreviation,
          },
        },
        getLabel: (opt: IStateAutocomplete) => opt?.name,
        enabledOnFocus: false,
        isClearable: false,
      }}
    />
  ) : (
    <FormField
      name="address[state]"
      type={InputType.TEXT_INPUT}
      inputProps={{
        placeholder: t('labelState'),
      }}
    />
  );

  return (
    <Form
      title={isEdit ? t('titleUpdateProject') : t('titleCreateProject')}
      isOpen={isOpen}
      onClose={onClose}
      submitBtnLabel={isEdit ? t('buttonLabelSave') : t('buttonLabelCreate')}
      cancelBtnLabel={t('buttonLabelCancel')}
      onSubmit={onSubmit}
      level={level}
    >
      <FormField
        name="name"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelProject'),
          required: true,
        }}
      />
      <FormGroupWrapper name="countryStateGroup" label={t('labelLocation')}>
        <FormField
          name="address[country]"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            placeholder: t('labelCountry'),
            optionKey: 'abbreviation',
            disableClearable: true,
            getOptions: addressService.getCountriesAutocomplete,
            getLabel: (option: ICountryAutocomplete) => option?.name,
            enabledOnFocus: false,
          }}
        />
        {StateField}
      </FormGroupWrapper>
      <FormGroupWrapper name="postalCodeCityGroup">
        <FormField
          name="address[zip]"
          type={InputType.TEXT_INPUT}
          inputProps={{
            placeholder: t('labelPostalCode'),
          }}
        />
        <FormField
          name="address[city]"
          type={InputType.TEXT_INPUT}
          inputProps={{
            placeholder: t('labelCity'),
          }}
        />
      </FormGroupWrapper>
      <FormField
        name="estimatedValue"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          label: t('labelEstimatedValue'),
          required: Boolean(user.companySettings?.projectEstimatedValueRequired),
          slotProps: {
            input: {
              decimalScale: 2,
            },
          },
        }}
      />
      <FormField
        name="closeConfidence"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          label: t('labelCloseConfidence'),
          required: Boolean(user.companySettings?.projectCloseConfidenceRequired),
          slotProps: {
            input: {
              min: 0,
              max: 100,
              allowedDecimalSeparators: [],
              decimalScale: 0,
              suffix: '%',
            },
          },
        }}
      />
      <FormField
        name="closeDate"
        type={InputType.SELECT}
        inputProps={{
          label: t('labelCloseDate'),
          required: Boolean(user.companySettings?.projectEstimatedCloseDateRequired),
          options: getDateOptions(),
        }}
      />
      <FormField
        name="verticalMarket"
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          label: t('labelVerticalMarket'),
          required: Boolean(user.companySettings?.verticalMarketRequired),
          getOptions: corporateService.getVerticalMarketsAutocomplete,
          getLabel: (option: IVerticalMarket) => option?.name,
          enabledOnFocus: false,
        }}
      />
      <FormField
        name="competitors"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelCompetitors'),
        }}
      />
      <FormGroupWrapper name="validDates" label={t('labelValid')}>
        <FormField
          name="validSince"
          type={InputType.DATE_INPUT}
          inputProps={{
            maxDate: watch('validUntil'),
          }}
        />
        <FormField
          name="validUntil"
          type={InputType.DATE_INPUT}
          inputProps={{
            minDate: watch('validSince'),
          }}
        />
      </FormGroupWrapper>
    </Form>
  );
};
